import { useEffect, useRef, useState } from 'react';

type Options = {
  /** Fire once and stop observing. */
  once?: boolean;
  rootMargin?: string;
  threshold?: number;
};

/**
 * Shared viewport observer. Returns a ref to attach and whether the element has
 * entered. `inView` starts false but nothing in this codebase *hides* content
 * on that basis — it only gates decorative animation.
 */
export function useInView<T extends HTMLElement>({
  once = true,
  rootMargin = '0px 0px -10% 0px',
  threshold = 0.15,
}: Options = {}) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);

  return { ref, inView };
}
